import {
  BUTTON_BASE,
  BUTTON_CHROME,
  BUTTON_VARIANT,
  FOCUS_RING,
  FOCUS_RING_INVERSE,
} from '../button/variants';

export type LinkType =
  | 'default'
  | 'mono'
  | 'wrapper'
  | 'button'
  | 'button-secondary'
  | 'button-secondary-purple'
  | 'button-yellow'
  | 'button-yellow-ghost'
  | 'button-purple-ghost';

/** Plain inline link. */
const TEXT_BASE =
  'font-medium text-link-static underline transition-[color] duration-200 ease-[ease] ' +
  'hover:cursor-pointer hover:text-link-hover ' +
  `${FOCUS_RING} ` +
  'active:cursor-pointer active:text-link-active';

/**
 * The button-shaped link types reuse the button chrome and drop the underline.
 *
 * The yellow and ghost variants were previously hardcoded hexes (#fedb63,
 * #10284b) that duplicated base-yellow and base-blue exactly; they use the
 * tokens now. They also transition opacity rather than background-color, which
 * is why they restate the transition.
 */
const BUTTON_LINK = `${BUTTON_BASE} no-underline`;

/*
  The yellow variants only ever render on the purple-500 and blue heroes, where
  the light ring drops to 1.34:1 and 2.14:1, so they carry the inverse one and
  callers do not have to remember.

  Built from BUTTON_CHROME rather than BUTTON_LINK on purpose — BUTTON_LINK
  already carries FOCUS_RING, and an element holding both colour utilities is
  decided by stylesheet order rather than by which was written last.
*/
const BUTTON_LINK_INVERSE = `${BUTTON_CHROME} ${FOCUS_RING_INVERSE} no-underline`;

const OPACITY_FADE =
  'whitespace-nowrap transition-opacity duration-200 ease-[ease] hover:opacity-90 active:opacity-90';

export const LINK_VARIANT: Record<LinkType, string> = {
  default: TEXT_BASE,

  mono: `${TEXT_BASE} font-mono text-base font-bold tracking-[0.07em] uppercase leading-[140%]`,

  wrapper: `${TEXT_BASE} no-underline`,

  button: `${BUTTON_LINK} ${BUTTON_VARIANT.primary}`,

  'button-secondary': `${BUTTON_LINK} ${BUTTON_VARIANT.secondary}`,

  'button-secondary-purple': `${BUTTON_LINK} border-button-primary bg-transparent text-button-primary hover:border-button-primary hover:bg-button-primary hover:text-type-white active:border-button-primary-active active:bg-button-primary-active active:text-type-white`,

  'button-yellow': `${BUTTON_LINK_INVERSE} ${OPACITY_FADE} bg-base-yellow text-base-blue hover:text-base-blue active:text-base-blue`,

  'button-yellow-ghost': `${BUTTON_LINK_INVERSE} ${OPACITY_FADE} border-base-yellow bg-transparent text-base-yellow hover:bg-base-yellow hover:text-base-blue active:bg-base-yellow active:text-base-blue`,

  'button-purple-ghost': `${BUTTON_LINK} ${OPACITY_FADE} border-button-primary bg-transparent text-button-primary hover:bg-button-primary hover:text-base-bone active:bg-button-primary active:text-base-bone`,
};

/**
 * Ready-made class string for the plain button-shaped link. The pages carry
 * ~125 raw <a> elements that used `className="link link--button"` instead of
 * the Link component; they import this so there is still one definition of the
 * look. They stay raw anchors on purpose — routing them through <Link> would
 * start firing its click-tracking call and change the analytics record.
 */
export const LINK_BUTTON = `link ${LINK_VARIANT.button}`;
